import { getDiscountCalculationsQueryImperative } from '@common/features/discounts/gqlHooks/useGetDiscountCalculationsQuery';
import { getEventsQueryImperative } from '@common/features/fulfilableProducts/gqlHooks/useGetEventsQuery';
import { getVouchersQueryImperative } from '@common/features/fulfilableProducts/gqlHooks/useGetVouchersQuery';
import { pick } from '@utils/objects/pick';
import { some } from '@utils/some';
import { Item, Promo } from 'shared-types';
import { BasketDetails } from 'shared-types/Basket';

/**
 *
 * Augment displayed basket details with fulfilable details,
 * including details of active discounts for fulfilables.
 *
 */
export const attachFulfilablesToBasketDetails = async (
  basketDetails: BasketDetails,
  siteSlug: string,
  language = 'en',
  timezone = 'Europe/London'
): Promise<BasketDetails> => {
  const eventItems = (basketDetails?.items ?? [])
    .filter((i) => i.eventId)
    .map((i) => pick(i, 'eventId', 'eventOptionId', 'detailsBag', 'quantity'));

  const voucherItems = (basketDetails?.items ?? [])
    .filter((i) => i.voucherId)
    .map((i) => pick(i, 'voucherId', 'voucherOptionId', 'detailsBag', 'quantity'));

  if (!some(eventItems) && !some(voucherItems)) {
    return basketDetails;
  }

  const vouchers = some(voucherItems)
    ? (
        await getVouchersQueryImperative({
          dto: {
            ids: voucherItems.map((vi) => Number(vi.voucherId)),
            language,
            siteSlug,
            defaultPropertyLanguage: language,
            paginationConfig: {
              limitPerPage: 200,
              page: 1,
            },
          },
        })
      )?.vouchers?.data
    : [];

  const events = some(eventItems)
    ? (
        await getEventsQueryImperative({
          dto: {
            ids: eventItems.map((ei) => Number(ei.eventId)),
            language,
            siteSlug,
            defaultPropertyLanguage: language,
            paginationConfig: {
              limitPerPage: 200,
              page: 1,
            },
          },
        })
      )?.events?.data
    : [];

  const activePromo = await _getActivePromoForFulfilables(
    basketDetails,
    [
      ...eventItems.map((e) => {
        const event = events?.find((x) => x.id == e.eventId);

        return {
          detailsBag: e.detailsBag,
          productId: event?.id ?? 0,
          productOptionId: e.eventOptionId,
          quantity: e.quantity,
          productType: 'Event' as const,
          categoryIds: event?.categoryIds ?? [],
        };
      }),
      ...voucherItems.map((v) => {
        const voucher = vouchers?.find((x) => x.id == v.voucherId);

        return {
          detailsBag: v.detailsBag,
          productId: voucher?.id ?? 0,
          productOptionId: v.voucherOptionId,
          quantity: v.quantity,
          productType: 'Voucher' as const,
          categoryIds: voucher?.categoryIds ?? [],
        };
      }),
    ].filter((x) => x),
    siteSlug,
    timezone
  );

  return {
    ...basketDetails,
    activePromo,
    items: (basketDetails?.items ?? []).map((i) => {
      const voucher = vouchers && vouchers.find((v) => v?.id && v.id == i.voucherId);

      if (voucher) {
        return {
          ...i,
          voucher,
          option: i.voucherOptionId
            ? voucher.options?.find((o) => o.id == i.voucherOptionId)
            : null,
        };
      }

      const event = events && events.find((e) => e?.id && e.id == i.eventId);

      if (event) {
        return {
          ...i,
          event,
          option: i.eventOptionId ? event.options?.find((o) => o.id == i.eventOptionId) : null,
        };
      }

      return i;
    }),
  };
};

/**
 * OJ-API can apply discounts to the basket based on calculation results,
 * but cannot currently determine for display purposes if there's an active promo
 * that only affects fulfilables, as the active_promo_id that it stores is only
 * for promos in its own domain.
 *
 * This is a fallback, therefore, which:
 * - sees if there's a promo code on the basket,
 * - but no active promo code,
 * - and if so,
 * - then it checks if there's a promo code causing price reductions to fulfilables in this basket.
 *
 * If there is, then activePromo is set.
 */
const _getActivePromoForFulfilables = async (
  basketDetails: BasketDetails,
  items: (Pick<Item, 'detailsBag' | 'quantity'> & {
    productId: number;
    productOptionId?: number;
    productType: 'Voucher' | 'Event';
    categoryIds: number[];
  })[],
  siteSlug: string,
  timezone: string
): Promise<{ name: string; discountValue: Promo } | null> => {
  // If there's no promo code on the basket anymore,
  // then there's definitely no activePromo anymore either
  if (!basketDetails?.promoCode) return null;

  if (basketDetails?.activePromo) return basketDetails.activePromo;

  const { currencyCode } = basketDetails;

  const promoCode = basketDetails.promoCode.toUpperCase();

  const result = await getDiscountCalculationsQueryImperative({
    dto: {
      possiblePurchases: items.map((i) => ({
        cannotBeDiscounted: false,
        fullPrice: (i.detailsBag?.discount?.fullPriceAsInt ?? 0) / 100,
        itemQuantity: i.quantity || 1,
        productId: i.productId,
        productOptionId: i.productOptionId,
        productType: i.productType,
        categoryIds: i.categoryIds,
      })),
      promoCode,
      propertyTimezone: timezone,
      siteSlug,
    },
  });

  if (!result?.discountCalculations?.possiblePurchasesWithDiscount) {
    return null;
  }

  const { possiblePurchasesWithDiscount } = result.discountCalculations;

  // Each possible purchase comes attached with the discount which is
  // reducing it.
  //
  // Look for a possible purchase which is being
  // reduced by the promo code on this basket.
  //
  // And if found, return the details of that promo
  const matchingPromo = (possiblePurchasesWithDiscount || []).find(
    (p) =>
      p.discount?.promoCode &&
      p.discountedPrice < p.discountedPriceOffersOnly &&
      p.discount.promoCode == promoCode
  )?.discount;

  if (!matchingPromo) return null;

  switch (matchingPromo.priceReduction.type.toLowerCase()) {
    case 'percentage': {
      return {
        name: matchingPromo.name,
        discountValue: {
          value: matchingPromo.priceReduction.value,
          type: 'percentage',
        },
      };
    }
    case 'fixed':
    case 'price':
    default: {
      return {
        name: matchingPromo.name,
        discountValue: {
          value: { amount: matchingPromo.priceReduction.value, currencyCode },
          type: 'price',
        },
      };
    }
  }
};
